import React from "react";

import {
  Grid2 as Grid,
  Typography,
  Box,
  Button,
  Link,
  Stack,
} from "@mui/material";
import { Card, CardBody, CardHeader } from "@progress/kendo-react-layout";
import { useTranslation } from "react-i18next";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import InfoIcon from "@mui/icons-material/Info";
import UndoIcon from "@mui/icons-material/Undo";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Print } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { DateTime } from "luxon";
import { useDispatch } from "react-redux";
import { closeDrawer } from "../../store";
import { useAppSelector } from "../../hooks/useAppSelector";

const PreviewSection = ({ title, icon: Icon, children }) => (
  <Box
    sx={{
      width: "100%",
      mb: 3,
      bgcolor: "background.paper",
      borderRadius: 1,
      p: 3,
      boxShadow: 1,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 2,
        pb: 1,
        borderBottom: 1,
        borderColor: "grey.200",
      }}
    >
      {Icon && <Icon sx={{ color: "primary.main", mr: 1 }} />}
      <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
    <Grid container spacing={3}>
      {children}
    </Grid>
  </Box>
);

const PreviewField = ({ label, value, data }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ color: "text.primary", fontWeight: 500 }}>
      {data ? data.find((d) => d.value === value)?.text : value || "-"}
    </Typography>
  </Box>
);

const JobApplicationPreview = ({ data, verified }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  const dispatch = useDispatch();
  const formatDate = (date) => {
    if (!date) return "-";
    return DateTime.fromISO(date, { zone: "Europe/Istanbul" })
      .setLocale(i18n.language)
      .toFormat("dd MMMM yyyy");
  };

  const yesNoData = React.useMemo(
    () => t("yes_no_data", { returnObjects: true }),
    [t]
  );
  const genderData = React.useMemo(
    () => t("gender_data", { returnObjects: true }),
    [t]
  );
  const militaryStatusData = React.useMemo(
    () => t("military_status_data", { returnObjects: true }),
    [t]
  );
  const educationLevelData = React.useMemo(
    () => t("education_level_data", { returnObjects: true }),
    [t]
  );
  const handlePrint = () => {
    dispatch(closeDrawer());
    setTimeout(() => {
      window.print();
    }, 1000);
  };
  return (
    <Card>
      <CardHeader>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", flexGrow: 1 }}>
            <VerifiedUserIcon sx={{ color: "primary.main", mr: 2 }} />
            <Typography variant="h5" sx={{ color: "text.primary" }}>
              {`${data.ad} ${data.soyad}`}
            </Typography>
          </div>

          {data.tekraR_ADAY_ID && isAuthenticated && (
            <Stack justifyContent="center" alignItems="center" mr={1}>
              <UndoIcon color="primary" />
              <Link to={`/list/${data.tekraR_ADAY_ID}`} component={RouterLink}>
                Önceki Başvuru
              </Link>
            </Stack>
          )}

          {((verified && !isAuthenticated) || isAuthenticated) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                padding: "8px 16px",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              {t("print")}
            </Button>
          )}
        </Box>
      </CardHeader>
      <CardBody>
        <Box sx={{ p: 1 }}>
          <PreviewSection
            title={t("jobApplication.preview.generalInfo")}
            icon={PersonIcon}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.personalInfo.fullName")}
                value={`${data.ad} ${data.soyad}`}
              />
              <PreviewField
                label={t("jobApplication.personalInfo.gender")}
                value={data.cinsiyet}
                data={genderData}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.personalInfo.birthDate")}
                value={formatDate(data.doguM_TARIHI)}
              />
              <PreviewField
                label={t("jobApplication.personalInfo.phone")}
                value={data.ceP_TEL_NO}
              />
            </Grid>
          </PreviewSection>
          <PreviewSection
            title={t("jobApplication.preview.personalInfo")}
            icon={PersonIcon}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.military.status")}
                value={data.askerlik}
                data={militaryStatusData}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.g_class")}
                value={data.g_LICENSE}
                data={yesNoData}
              />
            </Grid>
          </PreviewSection>
          <PreviewSection
            title={t("jobApplication.preview.address")}
            icon={LocationOnIcon}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.address.city")}
                value={data.il}
              />
              <PreviewField
                label={t("jobApplication.address.district")}
                value={data.ilce}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.address.fullAddress")}
                value={data.aciK_ADRES}
              />
            </Grid>
          </PreviewSection>

          <PreviewSection
            title={t("jobApplication.preview.education")}
            icon={SchoolIcon}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.education.level")}
                value={data.mezuniyet}
                data={educationLevelData}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.education.department")}
                value={data.bolum}
              />
            </Grid>
            <PreviewField
              label={t("jobApplication.certificates.title")}
              value={data.sertifikalar}
            />
          </PreviewSection>

          {data.deneyimler?.map((deneyim, i) => (
            <PreviewSection
              key={deneyim.soN_IS_FIRMA}
              title={t("jobApplication.lastJob.title", { i: i + 1 })}
              icon={WorkIcon}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <PreviewField
                  label={t("jobApplication.lastJob.company")}
                  value={deneyim.soN_IS_FIRMA}
                />
                <PreviewField
                  label={t("jobApplication.lastJob.position")}
                  value={deneyim.soN_IS_YAPILAN_IS}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PreviewField
                  label={t("jobApplication.lastJob.startDate")}
                  value={formatDate(deneyim.soN_IS_BAS_TAR)}
                />
                <PreviewField
                  label={t("jobApplication.lastJob.endDate")}
                  value={formatDate(deneyim.soN_IS_BIT_TAR)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PreviewField
                  label={t("jobApplication.lastJob.reasonForLeaving")}
                  value={deneyim.soN_IS_AYRILMA_NEDEN}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PreviewField label={"Not"} value={deneyim.adaY_NOT} />
              </Grid>
            </PreviewSection>
          ))}

          <PreviewSection
            title={t("jobApplication.preview.additionalInfo")}
            icon={InfoIcon}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.department")}
                value={
                  data.bolumler?.map((b) => b.dkN_DMY_BOLUM_ADI).join(", ") ||
                  "-"
                }
              />
              <PreviewField
                label={t("jobApplication.workPreferences.canWorkShifts")}
                value={data.vardiyalI_CALISIR}
                data={yesNoData}
              />
              <PreviewField
                label={t("jobApplication.workPreferences.canWorkAtHeight")}
                value={data.yuksektE_CALISIR}
                data={yesNoData}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PreviewField
                label={t("jobApplication.relatives.hasRelatives")}
                value={data.akrabasI_VAR}
                data={yesNoData}
              />
              {data.akrabasI_VAR === "EVET" && (
                <PreviewField
                  label={t("jobApplication.relatives.relation")}
                  value={data.akrabA_YAKINLIK}
                />
              )}
            </Grid>
          </PreviewSection>
        </Box>
      </CardBody>
    </Card>
  );
};

export default JobApplicationPreview;
